import {
  addDays,
  differenceInCalendarDays,
  differenceInMinutes,
  eachDayOfInterval,
  isValid,
  parse,
  parseISO,
  startOfDay,
} from 'date-fns'
import { IMeetingRoomSettingsDays } from './types'

export type MeetingRoomWeekdayKey = keyof IMeetingRoomSettingsDays

export const WEEKDAY_KEYS_BY_INDEX: readonly MeetingRoomWeekdayKey[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
]

const WEEKDAY_KEYS_MONDAY_FIRST: MeetingRoomWeekdayKey[] = [
  ...WEEKDAY_KEYS_BY_INDEX.slice(1),
  WEEKDAY_KEYS_BY_INDEX[0],
]

export const isDateWorkingDay = (
  date: Date,
  workingDays: IMeetingRoomSettingsDays
): boolean => {
  const key = WEEKDAY_KEYS_BY_INDEX[date.getDay()]
  return key != null && workingDays[key]
}

export const isDayAllowed = (
  dateStr: string,
  workingDays: IMeetingRoomSettingsDays
): boolean => {
  const [year, month, day] = dateStr.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  return isDateWorkingDay(date, workingDays)
}

export const addBusinessDays = (
  fromDate: Date,
  businessDays: number,
  workingDays: IMeetingRoomSettingsDays | null | undefined
): Date => {
  let current = startOfDay(fromDate)
  let remaining = Math.max(businessDays, 0)

  if (remaining === 0) return current

  while (remaining > 0) {
    current = addDays(current, 1)
    if (workingDays == null || isDateWorkingDay(current, workingDays)) {
      remaining -= 1
    }
  }

  return current
}

export const countInclusiveCalendarDays = (startDateStr: string, endDateStr: string): number => {
  const start = parse(startDateStr, 'yyyy-MM-dd', new Date())
  const end = parse(endDateStr, 'yyyy-MM-dd', new Date())
  return differenceInCalendarDays(end, start) + 1
}

export const getReservationDurationMinutes = (
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string
): number | null => {
  if (!startDate || !startTime || !endDate || !endTime) return null
  const start = parse(`${startDate} ${startTime}`, 'yyyy-MM-dd HH:mm', new Date())
  const end = parse(`${endDate} ${endTime}`, 'yyyy-MM-dd HH:mm', new Date())
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null
  return differenceInMinutes(end, start)
}

export const getClosedWeekdayKeys = (
  workingDays: IMeetingRoomSettingsDays
): MeetingRoomWeekdayKey[] => WEEKDAY_KEYS_MONDAY_FIRST.filter(day => !workingDays[day])

export const getWorkingDayIndexes = (workingDays: IMeetingRoomSettingsDays): number[] =>
  WEEKDAY_KEYS_BY_INDEX.reduce<number[]>((indexes, day, index) => {
    if (workingDays[day]) indexes.push(index)
    return indexes
  }, [])

export const getWorkingDaysFromIndexes = (
  workingDayIndexes: number[]
): IMeetingRoomSettingsDays => {
  const enabledIndexes = new Set(workingDayIndexes)

  return WEEKDAY_KEYS_BY_INDEX.reduce((workingDays, day, index) => {
    workingDays[day] = enabledIndexes.has(index)
    return workingDays
  }, {} as IMeetingRoomSettingsDays)
}

export const getClosedDatesInRange = (
  startDateStr: string,
  endDateStr: string,
  workingDays: IMeetingRoomSettingsDays
): Date[] => {
  if (!startDateStr || !endDateStr) return []

  const start = parseISO(startDateStr)
  const end = parseISO(endDateStr)

  if (!isValid(start) || !isValid(end) || end < start) return []

  return eachDayOfInterval({ start, end }).filter(date => !isDateWorkingDay(date, workingDays))
}
